// 🤝 PartnersPage - Page dédiée aux partenaires du festival
// Liste complète des partenaires organisés par catégorie
// Utilisé dans : src/App.jsx (route "/partners")

import { useState } from 'react'
import { Building2, Radio, Wrench, Award, ExternalLink, Users, Zap, Globe, X } from 'lucide-react'
import partnersData from '@/locales/partners.json'
import globalData from '@/locales/global.json'
import HeroSection from '@/components/home/HeroSection'

/**
 * Page partenaires du festival Rock'In Agde
 * Présente tous les partenaires organisés par catégories (institutionnels, médias, techniques, etc.)
 * Mapping dynamique des icônes selon la configuration dans partners.json
 */
export default function PartnersPage() {
  const { hero, categoriesConfig, categories, defaultDescription } = partnersData
  const { contact } = globalData
  
  // État pour gérer la modale
  const [selectedPartner, setSelectedPartner] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  // Fonction pour ouvrir la modale
  const openModal = (partner) => {
    setSelectedPartner(partner)
    setIsModalOpen(true)
  }
  
  // Fonction pour fermer la modale
  const closeModal = () => {
    setIsModalOpen(false)
    setTimeout(() => setSelectedPartner(null), 300)
  }

  // Map icon names to components
  const iconMap = {
    Building2,
    Radio,
    Wrench,
    Users,
    Zap,
    Globe
  }

  // Merge categoriesConfig with actual partner data
  const categoriesWithData = categoriesConfig.map(config => ({
    ...config,
    icon: iconMap[config.icon],
    partners: categories[config.key],
    color: 'primary'
  }))

  return (
    <div className="md:pt-16">
      {/* Hero Section */}
      <HeroSection
        badge={{ icon: Award, text: hero.badge }}
        title={hero.title}
        description={hero.description}
        features={hero.features.map(f => ({
          ...f,
          icon: f.text === "Institutionnels" ? Building2 : f.text === "Médias" ? Radio : Zap
        }))}
      />

      {/* Categories */}
      <section className="section-padding bg-white dark:bg-neutral-900">
        <div className="container-custom">
          <div className="space-y-16">
            {categoriesWithData.map((category, catIndex) => (
              <div key={catIndex} className="max-w-5xl mx-auto">
                {/* Category Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-4 bg-primary-100 dark:bg-primary-950 rounded-2xl">
                    <category.icon className="text-primary-600 dark:text-primary-400" size={32} />
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-display text-primary-600 dark:text-primary-400">
                      {category.title}
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-400">
                      {category.description}
                    </p>
                  </div>
                </div>

                {/* Partners Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.partners.map((partner, index) => {
                    const isHighlighted = partner.highlighted
                    const partnerName = typeof partner === 'string' ? partner : partner.name
                    const partnerDescription = typeof partner === 'string' ? defaultDescription : (partner.description || defaultDescription)
                    // Utiliser le flag clickable du JSON pour déterminer si modale
                    const isClickable = typeof partner === 'object' && partner.clickable === true
                    const showWebsite = typeof partner === 'object' && partner.showWebsiteOnCard === true
                    const hasWebsite = typeof partner === 'object' && partner.website && partner.website !== ""
                    
                    return (
                      <div
                        key={index}
                        onClick={() => isClickable && openModal(partner)}
                        className={`group bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl p-8 border-2 border-neutral-200 dark:border-neutral-700 transition-all shadow-lg ${
                          isClickable ? 'cursor-pointer' : 'cursor-default'
                        } ${
                          isClickable 
                            ? (isHighlighted 
                                ? 'hover:border-primary-500 dark:hover:border-primary-400 hover:shadow-[0_0_40px_rgba(167,28,0,0.4)] hover:scale-105' 
                                : 'hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-2xl hover:scale-105')
                            : ''
                        }`}
                                              >
                        {/* Logo placeholder */}
                        <div className={`aspect-video bg-gradient-to-br rounded-xl mb-4 flex items-center justify-center border transition-all ${
                          isHighlighted
                            ? 'from-neutral-100 to-neutral-200 dark:from-neutral-700 dark:to-neutral-800 border-neutral-300 dark:border-neutral-600 group-hover:shadow-[0_0_20px_rgba(167,28,0,0.3)]'
                            : 'from-neutral-100 to-neutral-200 dark:from-neutral-700 dark:to-neutral-800 border-neutral-300 dark:border-neutral-600'
                        }`}>
                          <category.icon className={`${
                            isHighlighted 
                              ? 'text-neutral-400 dark:text-neutral-500' 
                              : 'text-neutral-400 dark:text-neutral-500'
                          }`} size={48} />
                        </div>

                        {/* Partner Name */}
                        <h3 className={`font-bold text-lg mb-2 ${
                          isHighlighted
                            ? 'text-neutral-900 dark:text-neutral-100'
                            : 'text-neutral-900 dark:text-neutral-100'
                        }`}>
                          {partnerName}
                        </h3>

                        {/* Tagline */}
                        {typeof partner === 'object' && partner.tagline && (
                          <p className={`text-sm font-medium mb-2 ${
                            isHighlighted 
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-primary-600 dark:text-primary-400'
                          }`}>
                            {partner.tagline}
                          </p>
                        )}

                        {/* Description */}
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-2">
                          {partnerDescription}
                        </p>

                        {/* Website sur la carte (si showWebsiteOnCard = true) */}
                        {showWebsite && hasWebsite ? (
                          <a
                            href={partner.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline hover:gap-3 transition-all"
                          >
                            <Globe size={16} />
                            Visiter le site
                          </a>
                        ) : isClickable ? (
                          <button className="inline-flex items-center gap-2 hover:gap-3 transition-all text-sm font-medium text-primary-600 dark:text-primary-400">
                            En savoir plus
                            <ExternalLink size={16} />
                          </button>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-6">
            Vous souhaitez devenir partenaire ?
          </h2>
          <p className="text-xl text-neutral-100 mb-8 max-w-2xl mx-auto">
            Rejoignez l'aventure Rock'In Agde et associez votre image à l'événement musical de l'année !
          </p>
          <a
            href={`mailto:${contact.email}?subject=Demande de partenariat Rock'In Agde`}
            className="inline-flex items-center gap-2 bg-white text-primary-600 hover:bg-neutral-100 px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-xl hover:scale-105"
          >
            <Award size={24} />
            Nous contacter
          </a>
        </div>
      </section>

      {/* Modal */}
      {isModalOpen && selectedPartner && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={closeModal}
        >
          <div 
            className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 p-6 flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-display font-bold text-neutral-900 dark:text-neutral-100 mb-1">
                  {selectedPartner.name}
                </h2>
                {selectedPartner.tagline && (
                  <p className="text-primary-600 dark:text-primary-400 font-medium">
                    {selectedPartner.tagline}
                  </p>
                )}
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <X size={24} className="text-neutral-600 dark:text-neutral-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Description */}
              <div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                  À propos
                </h3>
                <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                  {selectedPartner.description || defaultDescription}
                </p>
              </div>

              {/* Expertise (si disponible) */}
              {selectedPartner.expertise && (
                <div className="bg-neutral-50 dark:bg-neutral-700 rounded-xl p-4 border-l-4 border-primary-500">
                  <p className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                    {selectedPartner.expertise}
                  </p>
                </div>
              )}

              {/* Services (si disponible) */}
              {selectedPartner.services && selectedPartner.services.length > 0 && (
                <div className="bg-neutral-50 dark:bg-neutral-700 rounded-xl p-6 border border-neutral-200 dark:border-neutral-600">
                  <h4 className="font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                    {selectedPartner.name === "Musicom" ? "Services" : "Services fournis"}
                  </h4>
                  <ul className="space-y-2 text-neutral-700 dark:text-neutral-200">
                    {selectedPartner.services.map((service, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-primary-600 dark:text-primary-400 mt-1">•</span>
                        <span>{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Website (si disponible et non vide) */}
              {selectedPartner.website && selectedPartner.website !== "" && (
                <div>
                  <a
                    href={selectedPartner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline font-medium"
                  >
                    <ExternalLink size={16} />
                    Visiter le site web
                  </a>
                </div>
              )}

              {/* Contact (si email ou phone disponible) */}
              {(selectedPartner.email || selectedPartner.phone) && (
                <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
                  <h4 className="font-bold text-neutral-900 dark:text-neutral-100 mb-3">Contact</h4>
                  <div className="space-y-2">
                    {selectedPartner.email && (
                      <p className="text-neutral-900 dark:text-neutral-300">
                        <span className="font-medium">Email:</span>{' '}
                        <a href={`mailto:${selectedPartner.email}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                          {selectedPartner.email}
                        </a>
                      </p>
                    )}
                    {selectedPartner.phone && (
                      <p className="text-neutral-900 dark:text-neutral-300">
                        <span className="font-medium">Téléphone:</span> {selectedPartner.phone}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 p-6">
              <button
                onClick={closeModal}
                className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
  )
}
